interface PerformanceMetrics {
  videoLoadTime: number;
  totalVideosLoaded: number;
  cacheHitRate: number;
  connectionSpeed: string;
  timestamp: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics[] = [];
  private videoLoadStartTimes: Map<string, number> = new Map();

  startVideoLoad(videoUrl: string) {
    this.videoLoadStartTimes.set(videoUrl, performance.now());
  }

  endVideoLoad(videoUrl: string, fromCache: boolean = false) {
    const startTime = this.videoLoadStartTimes.get(videoUrl);
    if (startTime) {
      const loadTime = performance.now() - startTime;
      this.videoLoadStartTimes.delete(videoUrl);
      
      // Log performance metric
      console.log(`Video loaded: ${videoUrl.split('/').pop()} in ${loadTime.toFixed(2)}ms ${fromCache ? '(from cache)' : '(network)'}`);
      
      return loadTime;
    }
    return 0;
  }

  recordMetrics(metrics: Partial<PerformanceMetrics>) {
    this.metrics.push({
      videoLoadTime: 0,
      totalVideosLoaded: 0,
      cacheHitRate: 0,
      connectionSpeed: 'unknown',
      timestamp: Date.now(),
      ...metrics
    });
  }

  getAverageLoadTime(): number {
    if (this.metrics.length === 0) return 0;
    const totalTime = this.metrics.reduce((sum, metric) => sum + metric.videoLoadTime, 0);
    return totalTime / this.metrics.length;
  }

  getCacheHitRate(): number {
    if (this.metrics.length === 0) return 0;
    const totalCacheHits = this.metrics.reduce((sum, metric) => sum + metric.cacheHitRate, 0);
    return totalCacheHits / this.metrics.length;
  }

  getPerformanceReport(): string {
    const avgLoadTime = this.getAverageLoadTime();
    const cacheHitRate = this.getCacheHitRate();
    const totalVideos = this.metrics.reduce((sum, metric) => sum + metric.totalVideosLoaded, 0);

    return `
Performance Report:
- Average video load time: ${avgLoadTime.toFixed(2)}ms
- Cache hit rate: ${(cacheHitRate * 100).toFixed(1)}%
- Total videos loaded: ${totalVideos}
- Metrics collected: ${this.metrics.length}
    `.trim();
  }

  logPerformanceReport() {
    console.log(this.getPerformanceReport());
  }

  clearMetrics() {
    this.metrics = [];
    this.videoLoadStartTimes.clear();
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Utility functions
export const logVideoLoadStart = (url: string) => {
  performanceMonitor.startVideoLoad(url);
};

export const logVideoLoadEnd = (url: string, fromCache: boolean = false) => {
  return performanceMonitor.endVideoLoad(url, fromCache);
};

export const logPerformanceReport = () => {
  performanceMonitor.logPerformanceReport();
}; 